import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Building2, Loader2, Users, Calendar, Megaphone, Trash2, Mail, LayoutDashboard, TrendingUp, Network, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCompany,
  useCompanyMembers,
  useCompanyInvitations,
  useCompanyStats,
  useInviteMember,
  useRemoveMember,
  useRevokeInvitation,
} from "@/hooks/useCompanies";
import { useCampaignsByCompany } from "@/hooks/useCampaigns";
import { DataPagination } from "@/components/shared/DataPagination";
import { toast } from "sonner";

export default function CompanyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [membersPage, setMembersPage] = useState(1);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const CAMPAIGNS_PER_PAGE = 10;
  const { data: company, isLoading: loadingCompany } = useCompany(id);
  const { data: membersData, isLoading: loadingMembers } = useCompanyMembers(id, membersPage);
  const members = membersData?.items ?? [];
  const { data: invitationsData, isLoading: loadingInvitations } = useCompanyInvitations(id, invitationsPage);
  const invitations = invitationsData?.items ?? [];
  const inviteMember = useInviteMember(id);
  const removeMember = useRemoveMember(id);
  const revokeInvitation = useRevokeInvitation(id);
  const { data: campaigns, isLoading: loadingCampaigns } = useCampaignsByCompany(company?.id);
  const { data: stats } = useCompanyStats(id);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("");

  if (loadingCompany || loadingMembers) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl font-semibold">Empresa não encontrada</h2>
      </div>
    );
  }

  function handleInviteSubmit() {
    if (!inviteEmail || !inviteRole) return;
    inviteMember.mutate(
      { email: inviteEmail, role: inviteRole },
      {
        onSuccess: () => {
          toast.success("Convite enviado com sucesso!");
          setInviteOpen(false);
          setInviteEmail("");
          setInviteRole("");
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao enviar convite";
          toast.error(msg);
        },
      }
    );
  }

  function handleRemoveMember(userId: number, email: string) {
    if (!confirm(`Remover ${email} da empresa?`)) return;
    removeMember.mutate(userId, {
      onSuccess: () => toast.success("Membro removido."),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao remover membro";
        toast.error(msg);
      },
    });
  }

  function handleRevokeInvitation(invitationId: number, email: string) {
    if (!confirm(`Revogar convite enviado para ${email}?`)) return;
    revokeInvitation.mutate(invitationId, {
      onSuccess: () => toast.success("Convite revogado."),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao revogar convite";
        toast.error(msg);
      },
    });
  }

  const statusLabel: Record<string, string> = {
    pending: "Pendente",
    accepted: "Aceito",
    revoked: "Revogado",
  };
  const statusVariant: Record<string, "default" | "secondary" | "destructive"> = {
    pending: "default",
    accepted: "secondary",
    revoked: "destructive",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary/10 rounded-xl text-primary">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{company.name}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setInviteOpen(true)}>
            <Users className="h-4 w-4" />
            Convidar Membro
          </Button>
          <Button className="gap-2" onClick={() => navigate(`/empresas/${id}/campanhas/nova`)}>
            <Megaphone className="h-4 w-4" />
            Nova Campanha
          </Button>
        </div>
      </div>

      <div>
        <Tabs defaultValue="visao-geral">
            <TabsList>
              <TabsTrigger value="visao-geral" className="gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Visão Geral
              </TabsTrigger>
              <TabsTrigger value="influenciadores" className="gap-2">
                <Users className="h-4 w-4" />
                Indicadores
                {(membersData?.total ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {membersData?.total}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="vendedores" className="gap-2">
                <Users className="h-4 w-4" />
                Vendedores
                {(membersData?.total ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {membersData?.total}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="convites" className="gap-2">
                <Mail className="h-4 w-4" />
                Convites
                {(invitationsData?.total ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {invitationsData?.total}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="campanhas" className="gap-2">
                <Megaphone className="h-4 w-4" />
                Campanhas
                {campaigns && campaigns.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{campaigns.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="visao-geral">
              <div className="grid gap-4 grid-cols-2 mt-0">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs uppercase font-semibold tracking-wider">Total de Campanhas</CardDescription>
                    <CardTitle className="text-3xl">{campaigns?.length ?? 0}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Megaphone className="h-3 w-3" />
                      Criadas nesta empresa
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs uppercase font-semibold tracking-wider">Campanhas Ativas</CardDescription>
                    <CardTitle className="text-3xl text-green-600">
                      {campaigns?.filter((c) => c.status === "ativa").length ?? 0}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Em andamento
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs uppercase font-semibold tracking-wider">Rede de Indicadores</CardDescription>
                    <CardTitle className="text-3xl">
                      {membersData?.total ?? 0}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Network className="h-3 w-3" />
                      Indicadores e vendedores
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs uppercase font-semibold tracking-wider">Total em Conversões</CardDescription>
                    <CardTitle className="text-2xl">
                      {stats ? (
                        <span className="flex flex-col gap-0.5">
                          {stats.conversoes_reais > 0 && (
                            <span>
                              {stats.total_valor_bruto_reais.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </span>
                          )}
                          {stats.conversoes_pontos > 0 && (
                            <span className={stats.conversoes_reais > 0 ? "text-xl" : ""}>
                              {stats.total_valor_bruto_pontos} pts
                            </span>
                          )}
                          {stats.total_conversions === 0 && "R$ 0,00"}
                        </span>
                      ) : "R$ 0,00"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {stats?.total_conversions ?? 0} conversões registradas
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {(["influenciadores", "vendedores"] as const).map((tab) => {
              const role = tab === "influenciadores" ? "influenciador" : "vendedor";
              const filtered = members?.filter((m) => m.role === role) ?? [];
              return (
                <TabsContent key={tab} value={tab}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg capitalize">{tab}</CardTitle>
                      <CardDescription>
                        {tab === "influenciadores"
                          ? "Influenciadores vinculados a esta empresa."
                          : "Vendedores com acesso a esta empresa."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingMembers ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : filtered.length > 0 ? (
                        <>
                          <div className="space-y-3">
                            {filtered.map((member) => (
                              <div key={member.usuario_id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">
                                    {member.email.substring(0, 2).toUpperCase()}
                                  </div>
                                  <p className="text-sm font-medium">{member.email}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Desde {new Date(member.joined_at).toLocaleDateString()}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleRemoveMember(member.usuario_id, member.email)}
                                    disabled={removeMember.isPending}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4">
                            <DataPagination
                              page={membersData?.page ?? 1}
                              pages={membersData?.pages ?? 1}
                              onPageChange={setMembersPage}
                            />
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-6">
                          Nenhum {role} encontrado.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}

            <TabsContent value="convites">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Convites Enviados</CardTitle>
                  <CardDescription>Histórico de convites para esta empresa.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingInvitations ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : invitations && invitations.length > 0 ? (
                    <>
                      <div className="space-y-3">
                        {invitations.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                                <Mail className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{inv.email}</p>
                                <p className="text-xs text-muted-foreground capitalize">{inv.role}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={statusVariant[inv.status] ?? "secondary"}>
                                {statusLabel[inv.status] ?? inv.status}
                              </Badge>
                              {inv.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => handleRevokeInvitation(inv.id, inv.email)}
                                  disabled={revokeInvitation.isPending}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4">
                        <DataPagination
                          page={invitationsData?.page ?? 1}
                          pages={invitationsData?.pages ?? 1}
                          onPageChange={setInvitationsPage}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">Nenhum convite enviado ainda.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="campanhas">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Campanhas</CardTitle>
                  <CardDescription>Campanhas vinculadas a esta empresa.</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCampaigns ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : campaigns && campaigns.length > 0 ? (
                    <>
                      <div className="space-y-3">
                        {campaigns
                          .slice(
                            (campaignsPage - 1) * CAMPAIGNS_PER_PAGE,
                            campaignsPage * CAMPAIGNS_PER_PAGE
                          )
                          .map((campaign) => (
                            <div key={String(campaign.id)} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                              <div>
                                <p className="text-sm font-medium">{campaign.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {campaign.remuneracao_modelo === "percentual"
                                    ? `${campaign.remuneracao_valor}% por venda`
                                    : campaign.usa_pontos
                                    ? `${Math.round(Number(campaign.remuneracao_valor))} pts por venda`
                                    : `R$ ${Number(campaign.remuneracao_valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} por venda`}
                                  {campaign.desconto_pct ? ` · ${campaign.desconto_pct}% desconto` : ""}
                                </p>
                              </div>
                              <Badge variant={
                                campaign.status === "ativa" ? "default" :
                                campaign.status === "aguardando_aceite" ? "secondary" : "destructive"
                              }>
                                {campaign.status === "ativa" ? "Ativa" :
                                 campaign.status === "aguardando_aceite" ? "Aguardando" : "Encerrada"}
                              </Badge>
                            </div>
                          ))}
                      </div>
                      <div className="mt-4">
                        <DataPagination
                          page={campaignsPage}
                          pages={Math.ceil(campaigns.length / CAMPAIGNS_PER_PAGE)}
                          onPageChange={setCampaignsPage}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">Nenhuma campanha criada ainda.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
        </Tabs>
      </div>

      {/* Dialog: Convidar Membro */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar Membro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="invite-email">E-mail</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="email@exemplo.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-role">Função</Label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Selecione a função</option>
                <option value="influenciador">Indicador</option>
                <option value="vendedor">Vendedor</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleInviteSubmit}
              disabled={!inviteEmail || !inviteRole || inviteMember.isPending}
            >
              {inviteMember.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar Convite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
